/*
BLOT: This file basically controls the dynamic rendering of each and every blog post.
The basic idea is that we render HTML dynamically using JavaScript and this is what react helps with.
*/

// Curly braces i.e. {} means import the named exports from those modules
// You can import a exported variable, function, class or object from other modules/components.
import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types' // PropTypes will be used later to validate the props that are passed to the component
import { kebabCase } from 'lodash' // Lodash is a library that will be used to manipulate the tags
import { Helmet } from 'react-helmet' // React Helmet will be used to dynamically set the page title and meta description
import { graphql, Link } from 'gatsby' // Gatsby's graphql function will be used to query for the data we need, and Link will be used to link to other pages
import Layout from '../components/Layout' // This is the component that will be used to wrap the page
import Content, { HTMLContent } from '../components/Content' // This is the component that will render the page content
import useSiteMetadata from '../components/SiteMetadata'
import PostMetaDates from '../components/PostMetaDates'
import BlogPhotoCarousel from '../components/BlogPhotoCarousel'
import 'giscus'

// ViewCount: fetches GA4 screenPageViews from our Netlify function.
// Renders nothing until the fetch resolves, and hides itself silently on error.
const ViewCount = ({ slug }) => {
  const [views, setViews] = useState(null)

  useEffect(() => {
    if (!slug) return
    fetch(`/.netlify/functions/get-views?path=${encodeURIComponent(slug)}`)
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((data) => {
        if (data.views > 0) setViews(data.views)
      })
      .catch(() => {
        // Silently fail — view count is non-critical
      })
  }, [slug])

  if (views === null) return null

  return (
    <div className="icon-text-container view-count">
      {/* Eye icon */}
      <svg
        stroke="currentColor"
        fill="currentColor"
        strokeWidth="0"
        viewBox="0 0 576 512"
        height="1em"
        width="1em"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3z" />
      </svg>
      <p>{views.toLocaleString()} views</p>
    </div>
  )
}

ViewCount.propTypes = {
  slug: PropTypes.string,
}

const htmlEntities = {
  '&amp;': '&',
  '&quot;': '"',
  '&#39;': "'",
  '&lt;': '<',
  '&gt;': '>',
}

const decodeHtmlEntities = (value) =>
  value.replace(/&(#x?[0-9a-f]+|amp|quot|#39|lt|gt);/gi, (entity, code) => {
    const namedEntity = htmlEntities[entity.toLowerCase()]

    if (namedEntity) {
      return namedEntity
    }

    const radix = code.toLowerCase().startsWith('#x') ? 16 : 10
    const numericValue = parseInt(code.replace(/^#x?/i, ''), radix)
    return Number.isNaN(numericValue) || numericValue > 0x10ffff
      ? entity
      : String.fromCodePoint(numericValue)
  })

const formatTocLabel = (value) =>
  decodeHtmlEntities(value.replace(/<[^>]*>/g, '')).replace(/^\d+\.\s+/, '')

const extractTocItems = (tableOfContents) => {
  if (!tableOfContents) {
    return []
  }

  const linkPattern = /<a\s+href="([^"]+)">([\s\S]*?)<\/a>/g
  const items = []
  let match

  while ((match = linkPattern.exec(tableOfContents)) !== null) {
    items.push({
      href: match[1],
      label: formatTocLabel(match[2]),
    })
  }

  return items
}

const BlogTableOfContents = ({ tableOfContents }) => {
  const [isOpen, setIsOpen] = useState(true)
  const items = extractTocItems(tableOfContents)

  if (items.length < 2) {
    return null
  }

  return (
    <nav className="blog-toc" aria-label="Table of contents">
      <button
        className="blog-toc-toggle"
        type="button"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>In This Article</span>
        <span className="blog-toc-indicator" aria-hidden="true">
          {isOpen ? '-' : '+'}
        </span>
      </button>
      {isOpen ? (
        <ol className="blog-toc-list">
          {items.map((item) => (
            <li key={item.href}>
              <a href={item.href}>{item.label}</a>
            </li>
          ))}
        </ol>
      ) : null}
    </nav>
  )
}

const BlogKeyInsights = ({ keyPoints }) => {
  const [isOpen, setIsOpen] = useState(true)

  if (!keyPoints || keyPoints.length === 0) {
    return null
  }

  return (
    <nav className="blog-key-insights" aria-label="Key Insights">
      <button
        className="blog-key-insights-toggle"
        type="button"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>Key Insights</span>
        <span className="blog-key-insights-indicator" aria-hidden="true">
          {isOpen ? '-' : '+'}
        </span>
      </button>
      {isOpen ? (
        <div className="blog-key-insights-content">
          <ul className="blog-key-insights-list">
            {keyPoints.map((point, index) => (
              <li key={index} className="blog-key-insights-item">
                {point}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </nav>
  )
}

BlogKeyInsights.propTypes = {
  keyPoints: PropTypes.arrayOf(PropTypes.string),
}

const carouselMarkerPattern = /<!--\s*photo-carousel:\s*([a-z0-9-]+)\s*-->/gi

const BlogPostImageLightbox = ({ children }) => {
  const contentRef = useRef(null)
  const [expandedImage, setExpandedImage] = useState(null)

  useEffect(() => {
    const contentElement = contentRef.current

    if (!contentElement) {
      return undefined
    }

    const openImage = (event) => {
      const target = event.target

      if (typeof target.closest !== 'function') {
        return
      }

      const image =
        target.closest('img') ||
        (typeof target.querySelector === 'function'
          ? target.querySelector('img')
          : null)

      if (
        !image ||
        !contentElement.contains(image) ||
        image.closest('.blog-photo-carousel')
      ) {
        return
      }

      const imageLink = image.closest('a')

      if (imageLink && contentElement.contains(imageLink)) {
        event.preventDefault()
      }

      setExpandedImage({
        src: image.currentSrc || image.src || imageLink?.getAttribute('href'),
        alt: image.getAttribute('alt') || '',
        caption: image.getAttribute('title') || image.getAttribute('alt') || '',
      })
    }

    contentElement.addEventListener('click', openImage)

    return () => contentElement.removeEventListener('click', openImage)
  }, [])

  useEffect(() => {
    if (!expandedImage) {
      return undefined
    }

    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setExpandedImage(null)
      }
    }

    document.addEventListener('keydown', closeOnEscape)

    return () => document.removeEventListener('keydown', closeOnEscape)
  }, [expandedImage])

  return (
    <div className="post-content" ref={contentRef}>
      {children}
      {expandedImage ? (
        <div
          className="blog-photo-carousel-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Expanded image"
        >
          <button
            className="blog-photo-carousel-lightbox-backdrop"
            type="button"
            aria-label="Close expanded image"
            onClick={() => setExpandedImage(null)}
          />
          <div className="blog-photo-carousel-lightbox-content">
            <button
              className="blog-photo-carousel-lightbox-close"
              type="button"
              aria-label="Close expanded image"
              onClick={() => setExpandedImage(null)}
            >
              <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">
                <path d="m6 6 12 12M18 6 6 18" />
              </svg>
            </button>
            <img src={expandedImage.src} alt={expandedImage.alt} />
            {expandedImage.caption ? (
              <p className="blog-photo-carousel-lightbox-caption">
                {expandedImage.caption}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}

const BlogPostContent = ({ content, contentComponent, photoCarousels }) => {
  const PostContent = contentComponent || Content
  const carouselsById = (photoCarousels || []).reduce((index, carousel) => {
    if (carousel?.id) {
      index[carousel.id] = carousel
    }
    return index
  }, {})

  if (
    typeof content !== 'string' ||
    !photoCarousels ||
    photoCarousels.length === 0 ||
    !carouselMarkerPattern.test(content)
  ) {
    carouselMarkerPattern.lastIndex = 0
    return (
      <BlogPostImageLightbox>
        <PostContent className="post-content-fragment" content={content} />
      </BlogPostImageLightbox>
    )
  }

  carouselMarkerPattern.lastIndex = 0
  const blocks = []
  let lastIndex = 0
  let match

  while ((match = carouselMarkerPattern.exec(content)) !== null) {
    const before = content.slice(lastIndex, match.index)
    if (before) {
      blocks.push({
        type: 'html',
        value: before,
      })
    }

    blocks.push({
      type: 'carousel',
      id: match[1],
    })
    lastIndex = match.index + match[0].length
  }

  const after = content.slice(lastIndex)
  if (after) {
    blocks.push({
      type: 'html',
      value: after,
    })
  }

  return (
    <BlogPostImageLightbox>
      {blocks.map((block, index) => {
        if (block.type === 'carousel') {
          return (
            <BlogPhotoCarousel
              key={`${block.id}-${index}`}
              carousel={carouselsById[block.id]}
            />
          )
        }

        return (
          <PostContent
            key={`content-${index}`}
            className="post-content-fragment"
            content={block.value}
          />
        )
      })}
    </BlogPostImageLightbox>
  )
}

BlogPostImageLightbox.propTypes = {
  children: PropTypes.node,
}

BlogPostContent.propTypes = {
  content: PropTypes.node.isRequired,
  contentComponent: PropTypes.func,
  photoCarousels: PropTypes.arrayOf(PropTypes.object),
}

const RecommendedReading = ({ posts }) => {
  if (!posts || posts.length === 0) {
    return null
  }

  return (
    <section className="related-insights-section">
      <hr />
      <h3 className="related-insights-title">Recommended Reading</h3>
      <div className="related-insights-container">
        {posts.map((post) => {
          const title = post.frontmatter.title
          const readingTime = post.fields?.readingTime?.text
          const excerpt = post.frontmatter.description || post.excerpt
          const primaryTag = post.frontmatter.tags?.[0]

          return (
            <Link
              to={post.fields.slug}
              key={post.id}
              className="related-insight-card-link"
            >
              <article className="related-insight-card">
                <div className="related-insight-content">
                  <div className="related-insight-meta">
                    {primaryTag && (
                      <span className="related-insight-type">{primaryTag}</span>
                    )}
                    {primaryTag && readingTime && (
                      <span className="related-insight-divider">•</span>
                    )}
                    {readingTime && <span>{readingTime}</span>}
                  </div>
                  <h4 className="related-insight-card-title">{title}</h4>
                  {excerpt && (
                    <p className="related-insight-excerpt">{excerpt}</p>
                  )}
                </div>
              </article>
            </Link>
          )
        })}
      </div>
    </section>
  )
}

RecommendedReading.propTypes = {
  posts: PropTypes.arrayOf(PropTypes.object),
}

/* Here is the explanation for the code below:
1. We are declaring a function BlogPostTemplate that takes a set of parameters
2. content, contentComponent, description, tags, title, and helmet are the parameters
3. content and title are the only required parameters
4. contentComponent is a component that will be used to render the content of the blog post
5. helmet is a component that will be used to add meta tags to the page */
// eslint-disable-next-line
export const BlogPostTemplate = ({
  content,
  contentComponent,
  description,
  tags,
  title,
  helmet,
  date,
  updated,
  readingTimeValue,
  tableOfContents,
  keyPoints,
  relatedPosts,
  slug,
  photoCarousels,
}) => {
  const [isShareOpen, setIsShareOpen] = useState(false)
  const isBrowser = typeof window !== 'undefined'
  const url = isBrowser ? window.location.href : ''

  useEffect(() => {
    const closeMenu = (e) => {
      if (!e.target.closest('.share-dropdown')) {
        setIsShareOpen(false)
      }
    }
    document.addEventListener('click', closeMenu)
    return () => document.removeEventListener('click', closeMenu)
  }, [])

  useEffect(() => {
    if (!isBrowser) {
      return undefined
    }

    const applyGiscusTheme = () => {
      const activeTheme =
        document.documentElement.getAttribute('data-theme') === 'dark'
          ? 'dark'
          : 'light'
      const giscusWidget = document.querySelector('giscus-widget')

      if (giscusWidget) {
        giscusWidget.setAttribute('theme', activeTheme)
      }
    }

    applyGiscusTheme()

    const observer = new MutationObserver(applyGiscusTheme)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    })

    return () => observer.disconnect()
  }, [isBrowser])

  return (
    <section className="section-blog-posts">
      <section className="section">
        {helmet || ''}
        <div className="container content">
          <div className="columns">
            <div className="column is-10 is-offset-1">
              <div className="icon-text-group">
                <div className="icon-text-container">
                  <svg
                    stroke="currentColor"
                    fill="currentColor"
                    strokeWidth="0"
                    viewBox="0 0 448 512"
                    height="1em"
                    width="1em"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M0 464c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V192H0v272zm320-196c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12h-40c-6.6 0-12-5.4-12-12v-40zm0 128c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12h-40c-6.6 0-12-5.4-12-12v-40zM192 268c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12h-40c-6.6 0-12-5.4-12-12v-40zm0 128c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12h-40c-6.6 0-12-5.4-12-12v-40zM64 268c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12H76c-6.6 0-12-5.4-12-12v-40zm0 128c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12H76c-6.6 0-12-5.4-12-12v-40zM400 64h-48V16c0-8.8-7.2-16-16-16h-32c-8.8 0-16 7.2-16 16v48H160V16c0-8.8-7.2-16-16-16h-32c-8.8 0-16 7.2-16 16v48H48C21.5 64 0 85.5 0 112v48h448v-48c0-26.5-21.5-48-48-48z"></path>
                  </svg>
                  <PostMetaDates date={date} updated={updated} />
                </div>
                <div className="icon-text-container">
                  <svg
                    stroke="currentColor"
                    fill="currentColor"
                    strokeWidth="0"
                    viewBox="0 0 384 512"
                    height="1em"
                    width="1em"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M0 512V48C0 21.49 21.49 0 48 0h288c26.51 0 48 21.49 48 48v464L192 400 0 512z"></path>
                  </svg>
                  <p>{readingTimeValue}</p>
                </div>
                <ViewCount slug={slug} />
                {/* New share button container */}
                <div className="share-dropdown">
                  <button
                    className="icon-text-container share-button"
                    onClick={(e) => {
                      e.stopPropagation()
                      setIsShareOpen(!isShareOpen)
                    }}
                  >
                    <svg
                      stroke="currentColor"
                      fill="currentColor"
                      strokeWidth="0"
                      viewBox="0 0 24 24"
                      width="1em"
                      height="1em"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92c0-1.61-1.31-2.92-2.92-2.92z" />
                    </svg>
                    <span>Share</span>
                  </button>

                  {isShareOpen && (
                    <div className="share-menu">
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(url)
                          setIsShareOpen(false)
                        }}
                      >
                        <svg viewBox="0 0 24 24" width="20" height="20">
                          <path
                            fill="currentColor"
                            d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"
                          />
                        </svg>
                        Copy link
                      </button>
                      <a
                        href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(
                          url
                        )}&text=${encodeURIComponent(title)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <svg viewBox="0 0 24 24" width="20" height="20">
                          <path
                            fill="currentColor"
                            d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
                          />
                        </svg>
                        Share on X
                      </a>
                      <a
                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                          url
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <svg viewBox="0 0 24 24" width="20" height="20">
                          <path
                            fill="currentColor"
                            d="M9.198 21.5h4v-8.01h3.604l.396-3.98h-4V7.5a1 1 0 0 1 1-1h3v-4h-3a5 5 0 0 0-5 5v2.01h-2l-.396 3.98h2.396v8.01z"
                          />
                        </svg>
                        Share on Facebook
                      </a>
                      <a
                        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                          url
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <svg viewBox="0 0 24 24" width="20" height="20">
                          <path
                            fill="currentColor"
                            d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"
                          />
                        </svg>
                        Share on LinkedIn
                      </a>
                    </div>
                  )}
                </div>
              </div>
              {tags && tags.length ? (
                <div className="blog-header-tags">
                  <Link to="/tags/" className="blog-header-tags-label">
                    Explore Tags:
                  </Link>
                  <ul className="blog-header-tags-list">
                    {tags.map((tag) => (
                      <li key={tag + `tag`}>
                        <Link to={`/tags/${kebabCase(tag)}/`}>{tag}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <hr></hr>
              <h1 className="title is-size-2 has-text-weight-bold is-bold-light">
                {title}
              </h1>
              <hr></hr>
              <p>{description}</p>
              <BlogKeyInsights keyPoints={keyPoints} />
              <BlogTableOfContents tableOfContents={tableOfContents} />
              <BlogPostContent
                content={content}
                contentComponent={contentComponent}
                photoCarousels={photoCarousels}
              />
              <RecommendedReading posts={relatedPosts} />
              <hr></hr>
              <giscus-widget
                id="comments"
                repo="optimsight/blog-giscus-comments"
                repoid="R_kgDOLKhl3Q"
                category="Announcements"
                categoryid="DIC_kwDOLKhl3c4Ccver"
                mapping="pathname"
                reactionsenabled="1"
                emitmetadata="0"
                inputposition="top"
                theme="light"
                lang="en"
                loading="lazy"
              ></giscus-widget>
            </div>
          </div>
        </div>
      </section>
    </section>
  )
}

BlogPostTemplate.propTypes = {
  content: PropTypes.node.isRequired,
  contentComponent: PropTypes.func,
  description: PropTypes.string,
  title: PropTypes.string,
  helmet: PropTypes.object,
  date: PropTypes.string,
  updated: PropTypes.string,
  readingTimeValue: PropTypes.string,
  tableOfContents: PropTypes.string,
  keyPoints: PropTypes.arrayOf(PropTypes.string),
  relatedPosts: PropTypes.arrayOf(PropTypes.object),
  slug: PropTypes.string,
  photoCarousels: PropTypes.arrayOf(PropTypes.object),
}

BlogTableOfContents.propTypes = {
  tableOfContents: PropTypes.string,
}

const BlogPost = ({ data, location }) => {
  const { markdownRemark: post, allMarkdownRemark } = data
  const { siteUrl } = useSiteMetadata()

  const currentTags = post.frontmatter.tags || []
  const currentSlug = post.fields.slug
  const metaTitle = post.frontmatter.metaTitle || post.frontmatter.title
  const metaDescription =
    post.frontmatter.metaDescription || post.frontmatter.description

  // Filter out current post
  const otherPosts = (allMarkdownRemark?.edges || [])
    .map(({ node }) => node)
    .filter((p) => p.fields.slug !== currentSlug)

  // Score other posts by tag overlap size
  const scoredPosts = otherPosts.map((p) => {
    const postTags = p.frontmatter.tags || []
    const sharedTags = postTags.filter((tag) => currentTags.includes(tag))
    return {
      post: p,
      score: sharedTags.length,
    }
  })

  // Sort by score desc, then by date desc
  scoredPosts.sort((a, b) => {
    if (b.score !== a.score) {
      return b.score - a.score
    }
    return new Date(b.post.frontmatter.date) - new Date(a.post.frontmatter.date)
  })

  // Slice top 2 related posts
  const relatedPosts = scoredPosts.slice(0, 2).map((sp) => sp.post)

  const schemaOrgJSONLD = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    url: `${siteUrl}${location.pathname}`,
    name: metaTitle,
    headline: metaTitle,
    description: metaDescription,
    image: post.frontmatter.featuredimage
      ? `${siteUrl}${post.frontmatter.featuredimage.publicURL}`
      : undefined,
    datePublished: post.frontmatter.date,
    dateModified: post.frontmatter.updated || post.frontmatter.date,
    publisher: {
      '@type': 'Organization',
      name: 'Optimsight',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/img/logo.svg`,
      },
    },
    author: {
      '@type': 'Organization',
      name: 'Optimsight Team',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}${location.pathname}`,
    },
  }

  return (
    <Layout>
      <BlogPostTemplate
        content={post.html}
        contentComponent={HTMLContent}
        description={post.frontmatter.description}
        date={post.frontmatter.date}
        updated={post.frontmatter.updated}
        readingTimeValue={post.fields.readingTime.text}
        tableOfContents={post.tableOfContents}
        keyPoints={post.frontmatter.keyPoints}
        photoCarousels={post.frontmatter.photoCarousels}
        relatedPosts={relatedPosts}
        helmet={
          <Helmet titleTemplate="%s | Optimsight">
            <title>{`${metaTitle}`}</title>
            <link rel="canonical" href={`${siteUrl}${location.pathname}`} />
            <meta property="og:type" content="article" />
            <meta property="og:title" content={`${metaTitle}`} />
            <meta property="og:description" content={`${metaDescription}`} />
            <meta
              property="article:published_time"
              content={`${post.frontmatter.date}`}
            />
            <meta
              property="article:modified_time"
              content={`${post.frontmatter.updated || post.frontmatter.date}`}
            />
            <meta property="og:site_name" content="Optimsight" />
            <meta
              property="og:url"
              content={`${siteUrl}${location.pathname}`}
            />
            <meta
              property="og:image"
              content={`${siteUrl}${post.frontmatter.featuredimage.publicURL}`}
            />
            <meta name="description" content={`${metaDescription}`} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:site" content="@optimsight" />
            <meta name="twitter:creator" content="@optimsight" />
            <meta name="twitter:title" content={`${metaTitle}`} />
            <meta name="twitter:description" content={`${metaDescription}`} />
            <meta
              name="twitter:image"
              content={`${siteUrl}${post.frontmatter.featuredimage.publicURL}`}
            />
            <script type="application/ld+json">
              {JSON.stringify(schemaOrgJSONLD)}
            </script>
          </Helmet>
        }
        tags={post.frontmatter.tags}
        title={post.frontmatter.title}
        slug={post.fields.slug}
      />
    </Layout>
  )
}

BlogPost.propTypes = {
  data: PropTypes.shape({
    markdownRemark: PropTypes.object,
  }),
}

export default BlogPost

// Get the Markdown file data
/* Here is the explanation for the code below:
1. This query is used by the page template to get the data it needs.
2. It uses the id variable from createPage() to get the markdown post.
3. The id variable is passed into the query as $id and results in the query being run once per post.
4. The result is stored in the markdownRemark variable, as specified in the query.
5. Then, the id, html, and frontmatter fields are pulled out of the markdownRemark variable and used in the page template.
6. The markdownRemark id is used as the react key, and the frontmatter title is used in the page header.
7. The markdownRemark html is used to render the page body. */
export const pageQuery = graphql`
  query BlogPostByID($id: String!) {
    markdownRemark(id: { eq: $id }) {
      id
      html
      tableOfContents(maxDepth: 2)
      frontmatter {
        date(formatString: "MMMM DD, YYYY")
        updated(formatString: "MMMM DD, YYYY")
        title
        metaTitle
        description
        metaDescription
        tags
        keyPoints
        photoCarousels {
          id
          label
          images {
            image {
              publicURL
            }
            alt
            caption
          }
        }
        featuredimage {
          childImageSharp {
            gatsbyImageData(layout: FIXED)
          }
          publicURL
        }
      }
      fields {
        slug
        readingTime {
          text
        }
      }
    }
    allMarkdownRemark(
      sort: { order: DESC, fields: [frontmatter___date] }
      filter: { frontmatter: { templateKey: { eq: "blog-post" } } }
    ) {
      edges {
        node {
          id
          fields {
            slug
            readingTime {
              text
            }
          }
          frontmatter {
            title
            description
            date(formatString: "MMMM DD, YYYY")
            tags
            featuredimage {
              childImageSharp {
                gatsbyImageData(width: 240, height: 160, layout: CONSTRAINED)
              }
              publicURL
            }
          }
        }
      }
    }
  }
`
